// @ts-strict-ignore
import { Connection } from '@3d-scene/model3d/connection';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { TextSizeCheckRequestVO } from '@3d-scene/manager/text-size-check.vo';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { Log } from '@shared/debug/log';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { BaseDynamicLabelingArticleConnectProcessor } from '../../../article/processor/prepare/connect/base-dynamic-labeling-article-connect.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ITableProvider } from '@project/components/label/label-editor/table-provider';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ProjectModel } from '@project/model/project.model';
import { OverlayService } from '@shared/overlay/overlay.service';
import { TableProviderGenerator } from '@project/components/label/label-editor/service/table-provider-generator';
import { Dictionary } from 'typescript-collections';
import { LabelingTextureData } from '../../labeling-texture-data';
import { IOperation } from '@shared/undo/operation/i-operation';
import { UndoConnectionLabelOperation } from '@shared/undo/operation/undo-connection-label-operation';
import { LabelSequenceOperation } from '@shared/undo/operation/label-sequence-operation';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { takeUntil } from 'rxjs/operators';
import { lastValueFrom, Subject } from 'rxjs';
import { AsideLabelEditorData } from '../aside-label-editor/aside-label-editor.component';
import { LabelEditorStateManager } from './label-editor-state.manager';
import { LabelEditorMode } from '../label-editor.component';
import { UnusedLabelingService } from './unused-labeling.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Injectable } from '@angular/core';
import { ProServeProjectImportProcessor } from '../../../import-export/processor/pro-serve-project-import.processor';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { IsProjectType } from '@project/model/is-project-type';
import { UserUtils } from '@user/service/user-utils';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';

export interface LabelEditorData {
  connectionCubes: Connection[];
  validateSizes: boolean;
  selectAllCellsOnInit: boolean;
}

@Injectable({ providedIn: 'root' })
export class OpenLabelEditorService {
  // Maximal recommended lenght for WMB marking; doubled compared to Flex version
  private readonly MAX_DATA_PROVIDER_SIZE: number = 210 * 4;

  private unusedLabelingCancelSubject = new Subject<void>();

  private _unusedLabelingService: UnusedLabelingService;
  private initialized = false;

  public initialize(unusedLabelingService: UnusedLabelingService) {
    this._unusedLabelingService = unusedLabelingService;
    this.initialized = true;
  }

  public async openForData(data: LabelEditorData): Promise<void> {
    if (this.proServeProjectImportProcessor.interceptOpenLabelEditorEvent(data)) {
      await this.execute(data.connectionCubes, data.validateSizes, data.selectAllCellsOnInit);
    }
  }

  constructor(
    private projectModel: ProjectModel,
    private textSizeCheckManager: TextSizeCheckManager,
    private tableProviderGenerator: TableProviderGenerator,
    private alertHelper: AlertHelper,
    private overlayService: OverlayService,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private labelEditorStateManager: LabelEditorStateManager,
    private labelingUtils: LabelingUtils,
    private connectionInfoService: ConnectionInfoService,
    private containerService: ContainerService,
    private proServeProjectImportProcessor: ProServeProjectImportProcessor,
    private textureConnectionUtils: TextureConnectionUtils,
  ) {
  }

  public async execute(
    connectionCubes: Connection[],
    validateSizes: boolean = true,
    selectAllCellsOnInit: boolean = false,
    pushUndo: boolean = true
  ): Promise<void> {
    if(!this.initialized) {
      throw new Error('OpenLabelEditorService not initialized!');
    }
    if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.CLOSED) {
      await this.overlayService.doWithOverlay(async() => {
        await this.executeInternal(connectionCubes, validateSizes, selectAllCellsOnInit, pushUndo);
      })
    } else {
      await this.executeInternal(connectionCubes, validateSizes, selectAllCellsOnInit, pushUndo);
    }
  }

  private async executeInternal(connectionCubes: Connection[], validateSizes: boolean, selectAllCellsOnInit: boolean, pushUndo: boolean) {
    this.unusedLabelingCancelSubject.next(undefined);

    const createdConnectionCubes$ = this._unusedLabelingService.createUnusedLabelingConnectionCubes(this.unusedLabelingCancelSubject)
      .pipe(takeUntil(this.unusedLabelingCancelSubject));
    const createdConnectionCubes = await lastValueFrom(createdConnectionCubes$, {defaultValue: null});
    if (!Array.isArray(createdConnectionCubes)) {
      // Got default-result, operation was cancelled.
      return;
    }
    connectionCubes = connectionCubes.concat(createdConnectionCubes);
    if (validateSizes) {
      await this.validateLabelEditorData(connectionCubes, selectAllCellsOnInit, pushUndo);
    } else {
      await this.continueExecution(connectionCubes, selectAllCellsOnInit, pushUndo);
    }
  }

  private async validateLabelEditorData(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): Promise<void> {
    if (this.isTooMuchMarkingContent(connectionCubes)) {
      if (this.isOnlyDynamicLabelingArticleSelected(connectionCubes)) {
        await this.validateDynamicLabelingArticlesLength(connectionCubes, selectAllCellsOnInit, pushUndo);
      } else {
        this.showTooMuchMarkingContentError();
      }
    } else {
      await this.validateDynamicLabelingArticlesLength(connectionCubes, selectAllCellsOnInit, pushUndo);
    }
  }

  private isTooMuchMarkingContent(connectionCubes: Connection[]): boolean {
    const maxMultilinePresent: number = LabelingUtils.getMaxAllowedMultilines(connectionCubes);
    return this.MAX_DATA_PROVIDER_SIZE / maxMultilinePresent < connectionCubes.length;
  }

  private showTooMuchMarkingContentError(): void {
    if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.CLOSED) {
      this.alertHelper.showError('labelEditorPopup.title.error_too_many_cells', 'labelEditorPopup.message.error_too_many_cells');
    } else {
      this.labelEditorStateManager.reportOpenLabelEditorError({
        title: 'labelEditorPopup.title.error_too_many_cells',
        message: 'labelEditorPopup.message.error_too_many_cells',
      });
    }
  }

  private isOnlyDynamicLabelingArticleSelected(connectionCubes: Connection[]): boolean {
    const articleContainers: ArticleContainer[] = this.getArticleContainersByConnections(connectionCubes);
    return articleContainers.length === 1 && ArticleUtils.isDynamicLabelingArticle(articleContainers[0].article);
  }

  private getArticleContainersByConnections(connectionCubes: Connection[]): ArticleContainer[] {
    const articleContainers: ArticleContainer[] = [];
    for (const cube of connectionCubes) {
      if (!articleContainers.includes(cube.articleContainer)) {
        articleContainers.push(cube.articleContainer);
      }
    }
    return articleContainers;
  }

  private async validateDynamicLabelingArticlesLength(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): Promise<void> {
    const invalidConnectionCubes: Connection[] = this.getConnectionCubesOfDynamicArticlesWithInvalidWidth(connectionCubes);
    if (ArrayUtils.isEmpty(invalidConnectionCubes)) {
      await this.continueExecution(connectionCubes, selectAllCellsOnInit, pushUndo);
    } else {
      await this.showTooLongDynamicLabelingArticleAlert(connectionCubes, invalidConnectionCubes, selectAllCellsOnInit, pushUndo);
    }
  }

  private getConnectionCubesOfDynamicArticlesWithInvalidWidth(connectionCubes: Connection[]): Connection[] {
    const articleContainers: ArticleContainer[] = this.getArticleContainersByConnections(connectionCubes);
    const result: Connection[] = [];
    for (const articleContainer of articleContainers) {
      if (this.isDynamicLabelingExceedsMaxWidth(articleContainer)) {
        result.push(...connectionCubes.filter((cube) => cube.articleContainer === articleContainer));
      }
    }
    return result;
  }

  private isDynamicLabelingExceedsMaxWidth(articleContainer: ArticleContainer): boolean {
    if (!ArticleUtils.isDynamicLabelingArticle(articleContainer.article)) {
      return false;
    }
    const width: number = ArticleContainerUtils.getDynamicArticleStretchLength(articleContainer);
    return width && BaseDynamicLabelingArticleConnectProcessor.MAX_LABELING_WIDTH < width;
  }

  private async showTooLongDynamicLabelingArticleAlert(connectionCubes: Connection[], invalidConnectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): Promise<void> {
    const answer = await this.alertHelper.showAlert(
      'connectProcessor.title.error_connect_too_long_labeling',
      'connectProcessor.message.error_connect_too_long_labeling',
      null, [AlertButton.YES, AlertButton.NO], "help");

    if (answer === AlertButton.NO) {
      connectionCubes = connectionCubes.filter(cube => !invalidConnectionCubes.includes(cube));
      if (this.labelEditorStateManager.labelEditorMode !== LabelEditorMode.CLOSED || !ArrayUtils.isEmpty(connectionCubes)) {
        await this.continueExecution(connectionCubes, selectAllCellsOnInit, pushUndo);
      }
    } else {
      await this.continueExecution(connectionCubes, selectAllCellsOnInit, pushUndo);
    }
  }

  private async continueExecution(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): Promise<void> {
    let commonTextSizeCheckRequests: TextSizeCheckRequestVO[] = [];
    const cubesToBuildRequest: Connection[] = this.labelingUtils.findMergedConnections(connectionCubes);
    if (this.isCommonRequest(cubesToBuildRequest)) {
      commonTextSizeCheckRequests = this.textSizeCheckManager.buildCommonTextSizeCheckRequest(cubesToBuildRequest);
    } else {
      commonTextSizeCheckRequests = this.textSizeCheckManager.buildTextSizeCheckRequest(cubesToBuildRequest);
    }

    await this.retrieveTextSizeCheckInfos(connectionCubes, selectAllCellsOnInit, pushUndo, commonTextSizeCheckRequests);
  }

  private isCommonRequest(cubesToBuildRequest: Connection[]): boolean {
    for (const cube of cubesToBuildRequest) {
      if (cube.metaElementVO && cube.metaElementVO instanceof TextureAreaVO && cube.articleContainer) {
        const article: ArticleVO = cube.articleContainer.article;
        return ArticleUtils.isDynamicLabelingArticle(article);
      }
    }
    return false;
  }

  private async retrieveTextSizeCheckInfos(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean,
                                     commonTextSizeCheckRequests: TextSizeCheckRequestVO[]): Promise<void> {
    if (commonTextSizeCheckRequests.length > 0) {
      const success = await this.textSizeCheckManager.retrieveTextSizeCheckInfos(commonTextSizeCheckRequests);
      this.textSizeCheckManagerRetrievalHandler(connectionCubes, selectAllCellsOnInit, pushUndo, success);
    } else {
      this.handleTextSizeCheckManagerRetrievalSuccess(connectionCubes, selectAllCellsOnInit, pushUndo);
    }
  }

  private textSizeCheckManagerRetrievalHandler(
    connectionCubes: Connection[],
    selectAllCellsOnInit: boolean,
    pushUndo: boolean,
    successRetrieval: boolean
  ): void {
    if (successRetrieval) {
      this.handleTextSizeCheckManagerRetrievalSuccess(connectionCubes, selectAllCellsOnInit, pushUndo);
    } else {
      this.handleTextSizeCheckManagerRetrievalFailure();
    }
  }

  private handleTextSizeCheckManagerRetrievalSuccess(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): void {
    const labelingArticles: ArticleVO[] = [];
    for (const connectionCube of connectionCubes) {
      if (connectionCube.article && !labelingArticles.find((article) => article.identity === connectionCube.article.identity)) {
        labelingArticles.push(connectionCube.article);
      }
    }

    this.prepareTableData(connectionCubes, selectAllCellsOnInit, pushUndo);
  }

  private handleTextSizeCheckManagerRetrievalFailure(): void {
    Log.error('TextSizeCheck failed')
  }

  private prepareTableData(connectionCubes: Connection[], selectAllCellsOnInit: boolean, pushUndo: boolean): void {
    const tableProviders: ITableProvider[] = this.tableProviderGenerator.generateGrouped(connectionCubes);
    if (ArrayUtils.isNotEmpty(tableProviders)) {
      const labelEditorTargetState: string =
        this.projectModel.user && this.projectModel.user.hasPermission("USE_ADVANCED_TEXT_EDIT_FUNCTIONS")
          ? 'advancedControl'
          : 'basicControl';
      const showDefaultLabelingBorderExplanation: boolean = IsProjectType.mcsGds(this.projectModel.currentProject);
      const asideLabelEditorData: AsideLabelEditorData = {
        tableProviders: tableProviders,
        labelEditorTargetState: labelEditorTargetState,
        showDefaultLabelingBorderExplanation: showDefaultLabelingBorderExplanation,
        markingSystemLabelModeEnabled: false,
        connectionCubes: connectionCubes,
        selectAllCellsOnInit: selectAllCellsOnInit,
        pushUndo: pushUndo,
      };
      this.labelEditorStateManager.openAsideLabelEditor(asideLabelEditorData);
    }
  }



  public createUndoLabelSequenceOperationForDirectPrinting(
    connectionCubes: Connection[],
    labelDataMap: Dictionary<string, LabelingTextureData>,
    detachedMetaElementsToCreateUndoOperation: TextureAreaUsageVO[] = []
  ): LabelSequenceOperation {
    if (!connectionCubes || !labelDataMap) {
      return;
    }

    const opers: IOperation[] = [];
    for (const cube of connectionCubes) {
      if (labelDataMap.containsKey(cube.uid)) {
        opers.push(new UndoConnectionLabelOperation(cube, labelDataMap.getValue(cube.uid), this.containerService));
      }
    }
    detachedMetaElementsToCreateUndoOperation.forEach((t) => {
      opers.push(new UndoConnectionLabelOperation({ metaElementUsage: t } as any, new LabelingTextureData(t), this.containerService));
    });
    if (opers.length > 0) {
      return new LabelSequenceOperation(
        opers,
        null,
        this.loadConnectionTextureProcessor,
        this.connectionInfoService,
        this.containerService,
        this.labelingUtils,
        null,
        this.projectModel,
        false
      );
    }
    return null;
  }

  public storeLabelConnectionsData(connectionCubes: Connection[]): Dictionary<string, LabelingTextureData> {
    if (!connectionCubes) {
      return null;
    }
    const cubeToTextureDataMap: Dictionary<string, LabelingTextureData> = new Dictionary<string, LabelingTextureData>();
    for (const cube of connectionCubes) {
      cubeToTextureDataMap.setValue(cube.uid, new LabelingTextureData(cube.metaElementUsage as TextureAreaUsageVO));
    }
    return cubeToTextureDataMap;
  }

  public openForCurrentSelection(): Promise<void> {
    return this.openForData({
      connectionCubes: this.getSelectedTextConnections(),
      validateSizes: true,
      selectAllCellsOnInit: false,
    });
  }

  private getSelectedTextConnections(): Connection[] {
    const onlyDefaultLabelingPermission: boolean = !UserUtils.hasPermissionToEditAllLabelings(this.projectModel.user);
    return this.textureConnectionUtils.getSelectedTextConnectionsWithRelative(onlyDefaultLabelingPermission);
  }
}
